import { useCallback, useEffect, useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'

/**
 * Runs `onTick` on an interval while the preview is on screen, until the
 * visitor touches it. Never runs for reduced-motion users.
 */
export function usePreviewAutoplay(onTick, intervalMs) {
  const reduceMotion = useReducedMotion()
  const ref = useRef(null)
  const tickRef = useRef(onTick)
  const [auto, setAuto] = useState(true)
  const [visible, setVisible] = useState(false)
  tickRef.current = onTick

  useEffect(() => {
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined') return undefined
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.35 })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const running = auto && visible && !reduceMotion

  useEffect(() => {
    if (!running) return undefined
    const id = window.setInterval(() => tickRef.current(), intervalMs)
    return () => window.clearInterval(id)
  }, [running, intervalMs])

  const stop = useCallback(() => setAuto(false), [])

  const interactionProps = {
    onPointerDown: stop,
    onKeyDown: stop,
    onWheel: stop,
    onTouchStart: stop,
  }

  return { ref, running, auto: auto && !reduceMotion, touched: !auto, stop, interactionProps }
}
